import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NotificationCenter = () => {
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      type: 'emergency',
      title: 'Emergency Booking Request',
      message: 'Water damage cleanup needed at 123 Main St. Customer willing to pay surge pricing.',
      time: '2 minutes ago',
      read: false,
      priority: 'high'
    },
    {
      id: 2,
      type: 'booking',
      title: 'New Booking Confirmation',
      message: 'Sarah Johnson confirmed deep cleaning service for tomorrow at 9:00 AM.',
      time: '15 minutes ago',
      read: false,
      priority: 'normal'
    },
    {
      id: 3,
      type: 'payment',
      title: 'Payment Received',
      message: 'Payment of $149.99 received from David Wilson for carpet cleaning service.',
      time: '1 hour ago',
      read: true,
      priority: 'normal'
    },
    {
      id: 4,
      type: 'staff',
      title: 'Staff Schedule Update',
      message: 'Mike Rodriguez requested time off for next Friday. Requires coverage assignment.',
      time: '2 hours ago',
      read: false,
      priority: 'normal'
    },
    {
      id: 5,
      type: 'inventory',
      title: 'Low Inventory Alert',
      message: 'Carpet cleaning solution running low. Only 3 units remaining in stock.',
      time: '3 hours ago',
      read: true,
      priority: 'medium'
    }
  ]);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'emergency':
        return 'AlertTriangle';
      case 'booking':
        return 'Calendar';
      case 'payment':
        return 'CreditCard';
      case 'staff':
        return 'Users';
      case 'inventory':
        return 'Package';
      default:
        return 'Bell';
    }
  };

  const getNotificationColor = (type, priority) => {
    if (priority === 'high' || type === 'emergency') {
      return 'text-error';
    }
    if (priority === 'medium') {
      return 'text-warning';
    }
    return 'text-primary';
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-error/10 text-error border-error/20';
      case 'medium':
        return 'bg-warning/10 text-warning border-warning/20';
      default:
        return 'bg-primary/10 text-primary border-primary/20';
    }
  };

  const markAsRead = (id) => {
    setNotifications(prev =>
      prev?.map(notification =>
        notification?.id === id ? { ...notification, read: true } : notification
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev =>
      prev?.map(notification => ({ ...notification, read: true }))
    );
  };

  const deleteNotification = (id) => {
    setNotifications(prev => prev?.filter(notification => notification?.id !== id));
  };

  const unreadCount = notifications?.filter(n => !n?.read)?.length;

  return (
    <div className="bg-white rounded-lg border border-border">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center space-x-2">
          <h3 className="text-lg font-semibold text-text-primary">Notifications</h3>
          {unreadCount > 0 && (
            <span className="bg-error text-white text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center">
              {unreadCount}
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={markAllAsRead}
            disabled={unreadCount === 0}
          >
            Mark All Read
          </Button>
          <Button variant="ghost" size="icon">
            <Icon name="Settings" size={16} />
          </Button>
        </div>
      </div>
      {/* Notifications List */}
      <div className="max-h-96 overflow-y-auto">
        {notifications?.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Bell" size={32} color="var(--color-text-secondary)" />
            </div>
            <h4 className="text-lg font-medium text-text-primary mb-2">No notifications</h4>
            <p className="text-text-secondary">You're all caught up!</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {notifications?.map((notification) => (
              <div
                key={notification?.id}
                className={`p-4 hover:bg-muted/30 transition-colors duration-200 ${
                  !notification?.read ? 'bg-primary/5' : ''
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    !notification?.read ? 'bg-primary/10' : 'bg-muted'
                  }`}>
                    <Icon 
                      name={getNotificationIcon(notification?.type)} 
                      size={16} 
                      color={getNotificationColor(notification?.type, notification?.priority)}
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-1">
                      <h4 className={`font-medium ${
                        !notification?.read ? 'text-text-primary' : 'text-text-secondary'
                      }`}>
                        {notification?.title}
                      </h4>
                      {notification?.priority !== 'normal' && (
                        <span className={`text-xs px-2 py-1 rounded-full border font-medium ${getPriorityBadge(notification?.priority)}`}>
                          {notification?.priority}
                        </span>
                      )}
                    </div>
                    
                    <p className={`text-sm mb-2 ${
                      !notification?.read ? 'text-text-primary' : 'text-text-secondary'
                    }`}>
                      {notification?.message}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-text-secondary">{notification?.time}</span>
                      <div className="flex items-center space-x-2">
                        {!notification?.read && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => markAsRead(notification?.id)}
                            className="text-xs"
                          >
                            Mark Read
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteNotification(notification?.id)}
                          className="w-6 h-6"
                        >
                          <Icon name="X" size={12} />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Footer */}
      {notifications?.length > 0 && (
        <div className="p-4 border-t border-border text-center">
          <Button variant="ghost" size="sm">
            View All Notifications
          </Button>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;